import { useGLTF } from '@react-three/drei';
import { Suspense, useEffect, useMemo, useRef } from 'react';
import { Color, Group, Mesh, MeshStandardMaterial, Object3D } from 'three';
import { GLTFErrorBoundary } from './GLTFErrorBoundary';
import { FALLBACK_BODY_BOUNDS, type BodyBounds } from './bodyBounds';
import { SkinnedAttachment } from './SkinnedAttachment';
import { useTuning } from './TuningContext';
import {
  DEFAULT_PROC_BELT, DEFAULT_PROC_SASH, DEFAULT_PROC_SHOULDER,
  getProceduralTune,
  type ItemMeshTune,
  type ProceduralBeltTune, type ProceduralSashTune, type ProceduralShoulderTune,
} from './tuningOverrides';
import { getToonRamp } from './ToonMaterial';
import { useObjMesh } from './useObjMesh';
import type { MeshAsset, MeshSlot } from './types';

/** Bone-name candidates per slot — matched against the rig's actual node names case-insensitively. */
// Three.js GLTFLoader strips dots/colons from node names: source `Wrist.R` → loaded `WristR`.
// Quaternius rigs in our packs use the dotless variant (`WristR`); we keep dotted/Mixamo
// variants as fallbacks for other rigs.
// Note: `body` slot never reaches MeshAttachment (PlayerCharacter handles it as a body-swap),
//       but it's part of the MeshSlot union so we provide no-op entries.
// All-lowercase entries cover rigs like KayKit Adventurers (head, wrist.l etc).
const SLOT_BONE_CANDIDATES: Record<MeshSlot, string[]> = {
  helmet:     ['Head', 'head', 'mixamorig:Head', 'Bone_Head', 'Bip01_Head'],
  // bone_weapon_r/l are dedicated weapon-mount bones on 3DSMax-Biped rigs (Cyber Knight) —
  // try those FIRST since they're the right pivot point. Falls through to generic Hand bones.
  hand_right: ['bone_weapon_r', 'WristR', 'Wrist.R', 'wrist.r', 'wrist_R', 'mixamorig:RightHand', 'Hand_R', 'hand.r', 'RightHand', 'Bip01_R_Hand'],
  hand_left:  ['bone_weapon_l', 'WristL', 'Wrist.L', 'wrist.l', 'wrist_L', 'mixamorig:LeftHand', 'Hand_L', 'hand.l', 'LeftHand', 'Bip01_L_Hand'],
  torso:      ['Chest', 'chest', 'mixamorig:Spine2', 'Spine', 'spine', 'Spine_2', 'Bip01_Spine1', 'Bip01_Spine'],
  // Empty bone-candidate lists for procedural slots: makes findBone() return
  // null, MeshAttachment falls back to mounting on the body root (`parent`).
  // Body-root local space is predictable (y=0 at feet, y grows up, +Z forward),
  // unlike per-rig bones whose origins / baked rotations vary asset-by-asset.
  // Trade-off: procedural pieces don't follow individual arm/torso animations,
  // but for static armour shapes that's correct — pauldrons stay on shoulders
  // even when the character runs, doesn't look like floating cardboard.
  belt:       [],
  sash:       [],
  shoulder:   [],
  body:       [],
};

/**
 * Per-slot default scale + offset tuned for Quaternius modular men + medieval weapons.
 * Quaternius weapons have origin at the grip with the blade extending along +Y (Blender Y-up).
 * On a Quaternius rig the wrist's +Y points along the forearm, so identity rotation lets
 * the blade extend naturally out of the hand — matching how the Sword_Slash anim was animated.
 */
const SLOT_DEFAULTS: Record<MeshSlot, { scale: number; offset: [number, number, number]; rotation: [number, number, number] }> = {
  hand_right: { scale: 0.20, offset: [0, 0, 0], rotation: [0, 0, 0] },
  hand_left:  { scale: 0.20, offset: [0, 0, 0], rotation: [0, 0, 0] },
  helmet:     { scale: 0.25, offset: [0, 0.05, 0], rotation: [0, 0, 0] },
  torso:      { scale: 1,    offset: [0, 0, 0],    rotation: [0, 0, 0] },
  body:       { scale: 1,    offset: [0, 0, 0],    rotation: [0, 0, 0] },
  // Procedural attachments are authored at world-scale, so default scale=1.
  // GLTF-based future items can override per-asset.
  belt:       { scale: 1, offset: [0, 0, 0], rotation: [0, 0, 0] },
  sash:       { scale: 1, offset: [0, 0, 0], rotation: [0, 0, 0] },
  shoulder:   { scale: 1, offset: [0, 0, 0], rotation: [0, 0, 0] },
};

/** Placeholder primitives when the asset is missing or still loading. */
function PlaceholderForSlot({ slot, tintColor }: { slot: MeshSlot; tintColor?: string }) {
  const color = tintColor ?? '#9aa0aa';
  switch (slot) {
    case 'helmet':
      return (
        <mesh castShadow position={[0, 0.05, 0]}>
          <sphereGeometry args={[0.13, 16, 12]} />
          <meshStandardMaterial color={color} metalness={0.4} roughness={0.5} />
        </mesh>
      );
    case 'hand_right':
    case 'hand_left':
      return (
        <mesh castShadow position={[0, 0.25, 0]} rotation={[0, 0, slot === 'hand_right' ? -0.4 : 0.4]}>
          <boxGeometry args={[0.04, 0.55, 0.04]} />
          <meshStandardMaterial color={color} metalness={0.6} roughness={0.3} />
        </mesh>
      );
    case 'torso':
      return (
        <mesh castShadow position={[0, 0, 0]}>
          <boxGeometry args={[0.45, 0.55, 0.28]} />
          <meshStandardMaterial color={color} metalness={0.3} roughness={0.6} />
        </mesh>
      );
    case 'body':
      // Body-slot is handled by PlayerCharacter as a body-glTF swap, not by MeshAttachment.
      return null;
  }
}

function findBone(root: Object3D, candidates: string[]): Object3D | null {
  // Empty list = caller intentionally wants the fallback (mount on parent).
  if (candidates.length === 0) return null;
  // Pass 1: exact name match on getObjectByName (covers most rigs in one shot).
  for (const c of candidates) {
    const direct = root.getObjectByName(c);
    if (direct && isBoneLike(direct)) return direct;
  }
  // Pass 2: prefer EXACT case-insensitive match on Bone-type objects only.
  // This is the critical fix for KayKit Adventurers where there's a MESH
  // called `Knight_Head` AND a BONE called `head`. Substring matching would
  // hit the mesh (depth-first) before the bone, mounting helmets at the
  // mesh's origin (= scene origin = ground) instead of at the head bone.
  const low = candidates.map((c) => c.toLowerCase());
  let exactBone: Object3D | null = null;
  let exactAny: Object3D | null = null;
  let substrBone: Object3D | null = null;
  let substrAny: Object3D | null = null;
  root.traverse((child) => {
    if (!child.name) return;
    const name = child.name.toLowerCase();
    const isBone = isBoneLike(child);
    if (low.some((c) => name === c)) {
      if (isBone && !exactBone) exactBone = child;
      if (!exactAny) exactAny = child;
    } else if (low.some((c) => name.includes(c))) {
      if (isBone && !substrBone) substrBone = child;
      if (!substrAny) substrAny = child;
    }
  });
  // Priority: exact-match bone > exact-match anything > substring bone >
  // substring anything. Bones always beat meshes-with-similar-names.
  const found = exactBone ?? exactAny ?? substrBone ?? substrAny;
  if (!found && typeof console !== 'undefined') {
    // Dedup: log once per (candidates × root) pair. On mobile WebView each
    // console.warn with a traversed descendant list costs 5-10 ms — spamming
    // it per attachment mount (one per equipped item, every wave) is a
    // measurable freeze contributor.
    const dedupKey = `${candidates.join('|')}::${root.name ?? '?'}`;
    if (!loggedBoneMisses.has(dedupKey)) {
      loggedBoneMisses.add(dedupKey);
      const allNames: string[] = [];
      root.traverse((c) => { if (c.name) allNames.push(c.name); });
      console.warn(
        '[MeshAttachment] no bone matched for candidates:', candidates,
        '\nparent root:', root.name, root.type,
        '\nall named descendants:', allNames,
      );
    }
  }
  return found;
}
const loggedBoneMisses = new Set<string>();
const loggedMountWarn = new Set<string>();

/** Three.js Bone instances have isBone=true; Object3Ds in armature trees set
 * isObject3D=true. We treat both Bone and parent-of-bones (the armature root)
 * as bone-like so Pass-1's getObjectByName still matches Quaternius-style
 * rigs where the helmet anchor is a regular Object3D, not a Bone. */
function isBoneLike(o: Object3D): boolean {
  if ((o as { isBone?: boolean }).isBone) return true;
  // For non-Bone Object3Ds — treat as bone-like only if the name looks rig-y
  // (Quaternius post-load names like 'WristR', 'Head' are plain Object3Ds
  // when imported as glTF nodes). If parent is the scene/armature, it's a
  // bone-like node.
  if ((o as { isMesh?: boolean }).isMesh) return false;
  return true;
}

function isObjPath(path: string) {
  return /\.obj($|\?)/i.test(path);
}

function isProceduralPath(path: string) {
  return path.startsWith('procedural://');
}

/** Inner component that loads a glTF mesh. Throws to Suspense on missing files. */
function GLTFLoader({ asset }: { asset: MeshAsset }) {
  const { scene } = useGLTF(asset.path);
  // Holds the CURRENT batch of tinted materials. On every memo recompute we
  // first dispose the previous batch (previous tint config), then build the
  // new one — this catches both equip-swaps (deps change) and unmount via
  // the final useEffect cleanup. Without disposal, every change leaks
  // GPU shader programs and uniform buffers.
  const ownedMatsRef = useRef<MeshStandardMaterial[]>([]);
  const tinted = useMemo(() => {
    for (const m of ownedMatsRef.current) m.dispose();
    const cloned = scene.clone(true);
    const owned: MeshStandardMaterial[] = [];

    // Tinting strategy:
    //   - If the asset's mesh-tree has any submesh whose name contains
    //     "_accent" (or "accent"), ONLY those get the rarity tint. Lets us
    //     ship items where e.g. a helmet's visor is the colored accent and
    //     the shell stays its natural cyber-steel.
    //   - Otherwise the tint LERPs into every material at a rarity-driven
    //     strength (subtle on common, vibrant on legendary). The previous
    //     code REPLACED the colour outright which made every common-rare
    //     piece read as the same flat tint and clashed with the body.
    let hasAccentMarkers = false;
    cloned.traverse((c) => {
      if ((c as Mesh).isMesh && /(^|[_\-.])accent($|[_\-.])/i.test(c.name ?? '')) {
        hasAccentMarkers = true;
      }
    });
    const lerpStrength = tintStrengthFor(asset.tintColor);

    cloned.traverse((child) => {
      if (!(child as Mesh).isMesh) return;
      const mesh = child as Mesh;
      mesh.castShadow = true;
      if (!asset.tintColor) return;
      const mat = mesh.material as MeshStandardMaterial;
      if (!mat || !('color' in mat)) return;
      const isAccent = /(^|[_\-.])accent($|[_\-.])/i.test(mesh.name ?? '');
      const m = mat.clone();
      const c = new Color(asset.tintColor);
      if (hasAccentMarkers) {
        // Asset is marked: tint accent submeshes hard, leave the rest alone.
        if (isAccent) m.color.copy(c);
      } else {
        // Legacy / unmarked asset: blend the tint into every material so the
        // original lighting variety is preserved.
        m.color.lerp(c, lerpStrength);
      }
      // Legendary glow: respect explicit emissive on the asset.
      if (asset.emissive && 'emissive' in m) {
        m.emissive = new Color(asset.emissive);
        if (asset.emissiveIntensity !== undefined) m.emissiveIntensity = asset.emissiveIntensity;
      }
      mesh.material = m;
      owned.push(m);
    });
    ownedMatsRef.current = owned;
    return cloned;
  }, [scene, asset.tintColor, asset.emissive, asset.emissiveIntensity]);
  useEffect(() => () => {
    for (const m of ownedMatsRef.current) m.dispose();
    ownedMatsRef.current = [];
  }, []);
  return <primitive object={tinted} />;
}

/** How aggressively the rarity tint blends into the asset's material colours.
 *  Mapped from the (uppercase) hex tint string back to a strength via the known
 *  RARITY_TINT table — keeps tint decisions co-located with the asset pipeline
 *  without coupling MeshAttachment to the Rarity union. */
const TINT_STRENGTH_BY_HEX: Record<string, number> = {
  '#C8D0D8': 0.20,  // common — barely tinted
  '#9AF0B8': 0.35,  // uncommon
  '#6AB4FF': 0.50,  // rare
  '#C87AFF': 0.65,  // epic
  '#FFD84A': 0.80,  // legendary
  '#FF4AF0': 0.92,  // mythic
  // Set tints land on EXACT colour (no LERP residue). Without this, a helm
  // GLTF with dark-grey baseline + a shield GLTF with light-grey baseline
  // both lerped 85 % toward void-purple produce visibly different shades. Set
  // pieces are supposed to read as ONE cohesive armour — full replace.
  '#2A2640': 1.00,  // hollow_knight set
};

function tintStrengthFor(tintColor: string | undefined): number {
  if (!tintColor) return 0;
  const key = tintColor.toUpperCase();
  return TINT_STRENGTH_BY_HEX[key] ?? 0.55;
}

/** Inner component that loads an OBJ + MTL mesh. */
function OBJLoaderEl({ asset }: { asset: MeshAsset }) {
  const obj = useObjMesh(asset.path, asset.tintColor);
  return <primitive object={obj} />;
}

// ── Procedural attachments ──────────────────────────────────────────────────
// Items in slots {shoulder, belt, sash} that ship without a GLTF render as
// simple toon-shaded primitives mounted on the matching bone. Lets us add
// new equipment SKUs without authoring a mesh per item.

interface ProceduralAttachmentProps {
  asset: MeshAsset;
  /** Measured silhouette of the parent body — drives proportional placement.
   *  Falls back to chibi defaults when the bbox isn't yet available. */
  bounds?: BodyBounds;
  /** Per-shape tuning overrides from the Equipment Studio. Each shape uses
   *  its own subset of fields; passed in as `unknown` and re-cast inside. */
  tune?: ProceduralShoulderTune | ProceduralBeltTune | ProceduralSashTune;
}

function ProceduralAttachment({ asset, bounds, tune }: ProceduralAttachmentProps) {
  const ramp = useMemo(() => getToonRamp(), []);
  const color = asset.tintColor ?? '#888a92';
  // emissiveIntensity from the asset drives the strength of the glow accents.
  // The emissive *colour* always tracks the tint colour (the rarity/set hue);
  // a separate asset.emissive override would fight the cohesive accent palette.
  const emissiveIntensity = asset.emissiveIntensity ?? 0;
  const b = bounds ?? FALLBACK_BODY_BOUNDS;

  // Path encodes which shape to render: procedural://shoulder | belt | sash
  const shape = asset.path.replace('procedural://', '') as 'shoulder' | 'belt' | 'sash';

  // X / Z dimensions: bbox.halfWidth would measure T-POSE ARM-SPAN for
  // skinned meshes, not the torso. Derive torso dimensions from the body
  // height — anatomy ratio shoulder-width-to-height is stable around 0.32
  // across chibi and realistic humanoids.
  const torsoHalfWidth = b.height * 0.16;
  const torsoHalfDepth = b.height * 0.11;

  if (shape === 'shoulder') {
    const t = (tune as ProceduralShoulderTune | undefined) ?? DEFAULT_PROC_SHOULDER;
    const shoulderY = b.feetY + b.height * t.yFrac;
    const xOffset   = torsoHalfWidth * t.xOffsetFrac;
    const capRadius = Math.max(0.07, torsoHalfWidth * t.capRadiusFrac);
    const accentEmissive = emissiveIntensity > 0 ? emissiveIntensity : 1.0;
    return (
      <group>
        {[-1, 1].map((side) => (
          <group
            key={side}
            position={[b.centerX + side * xOffset, shoulderY, b.centerZ]}
            rotation={[0, 0, side * -t.tiltZ]}
          >
            <mesh rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[capRadius, capRadius * 0.13, 6, 20]} />
              <meshBasicMaterial color={color} toneMapped={false} />
            </mesh>
            <mesh scale={[1, 0.5, 1]} castShadow>
              <sphereGeometry args={[capRadius * 0.85, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
              <meshToonMaterial
                color={color}
                gradientMap={ramp}
                emissive={color}
                emissiveIntensity={accentEmissive * 0.4}
              />
            </mesh>
          </group>
        ))}
      </group>
    );
  }

  if (shape === 'belt') {
    const t = (tune as ProceduralBeltTune | undefined) ?? DEFAULT_PROC_BELT;
    const beltY       = b.feetY + b.height * t.yFrac;
    const torusRadius = torsoHalfWidth * t.radiusFrac;
    const tube        = torusRadius * t.tubeFrac;
    const buckleW     = torusRadius * t.buckleSize;
    const buckleH     = tube * 2.6;
    return (
      <group position={[b.centerX, beltY, b.centerZ]}>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[torusRadius, tube, 6, 26]} />
          <meshBasicMaterial color={color} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0, torusRadius - tube]} castShadow>
          <boxGeometry args={[buckleW, buckleH, tube * 1.5]} />
          <meshToonMaterial color={color} gradientMap={ramp} emissive={color} emissiveIntensity={0.8} />
        </mesh>
      </group>
    );
  }

  if (shape === 'sash') {
    const t = (tune as ProceduralSashTune | undefined) ?? DEFAULT_PROC_SASH;
    const sashY     = b.feetY + b.height * t.yFrac;
    const length    = b.height * t.lengthFrac;
    const stripW    = Math.max(0.04, torsoHalfWidth * t.widthFrac);
    const stripD    = 0.018;
    const forwardZ  = b.centerZ + torsoHalfDepth * t.forwardFrac;
    const xOffset   = torsoHalfWidth * t.xOffsetFrac;
    return (
      <group position={[b.centerX + xOffset, sashY, forwardZ]} rotation={[0, 0, -t.tiltZ]}>
        <mesh castShadow>
          <boxGeometry args={[stripW, length, stripD]} />
          <meshToonMaterial
            color={color}
            gradientMap={ramp}
            emissive={color}
            emissiveIntensity={Math.max(0.25, emissiveIntensity)}
          />
        </mesh>
        <mesh position={[stripW * 0.45, 0, stripD * 0.6]}>
          <boxGeometry args={[stripW * 0.14, length, stripD * 0.7]} />
          <meshBasicMaterial color={color} toneMapped={false} />
        </mesh>
        <mesh position={[-stripW * 0.45, 0, stripD * 0.6]}>
          <boxGeometry args={[stripW * 0.14, length, stripD * 0.7]} />
          <meshBasicMaterial color={color} toneMapped={false} />
        </mesh>
      </group>
    );
  }

  return null;
}

interface Props {
  parent: Object3D | null;
  asset: MeshAsset;
  /** Body GLTF path — needed by procedural shapes to look up per-body tuning. */
  bodyPath?: string;
  /** Item id for tuning-override lookups (per-item GLTF transforms).
   *  When omitted, item tuning is bypassed (keeps non-tunable callers simple). */
  itemId?: string;
}

/**
 * Mounts an equipment mesh onto the right bone of the parent character.
 * Supports both glTF (skinned/skeletal) and OBJ (static) — picks the loader by file extension.
 * Falls back to a primitive placeholder when the asset is missing.
 */
export function MeshAttachment({ parent, asset, bodyPath, itemId }: Props) {
  const groupRef = useRef<Group>(null);
  const defaults = SLOT_DEFAULTS[asset.slot];
  const procedural = isProceduralPath(asset.path);
  const skinned = asset.kind === 'skinned';
  const tuning = useTuning();
  const itemTune: ItemMeshTune | undefined = itemId ? tuning.items[itemId] : undefined;

  useEffect(() => {
    // Procedural shapes render via JSX (no manual reparent).
    // Skinned attachments handle their own mounting in SkinnedAttachment.
    if (procedural || skinned) return;
    if (!parent || !groupRef.current) return;
    const candidates = SLOT_BONE_CANDIDATES[asset.slot];
    const bone = findBone(parent, candidates);
    const target = bone ?? parent;
    target.add(groupRef.current);
    if (!bone && candidates.length > 0 && typeof console !== 'undefined') {
      // Dedup per (slot, parent) so a recurring asset doesn't spam per mount.
      const dedupKey = `${asset.slot}::${parent?.name ?? '?'}`;
      if (!loggedMountWarn.has(dedupKey)) {
        loggedMountWarn.add(dedupKey);
        console.warn(`[MeshAttachment] slot=${asset.slot}: no bone found, mounted to parent`, parent?.name);
      }
    }
    return () => {
      target.remove(groupRef.current!);
    };
  }, [parent, asset.slot, asset.path, procedural, skinned]);

  const placeholder = <PlaceholderForSlot slot={asset.slot} tintColor={asset.tintColor} />;

  // Procedural shapes render as a normal R3F child — no manual scene-graph
  // manipulation. They sit in the same local frame as the body GLTF (sibling
  // under PlayerCharacter's outer <group>), so the bbox we measured in
  // cloned-local space drives their positions directly.
  if (procedural) {
    const bounds = parent?.userData?.bodyBounds as BodyBounds | undefined;
    const shape = asset.path.replace('procedural://', '') as 'shoulder' | 'belt' | 'sash';
    const tune = bodyPath ? getProceduralTune(tuning, bodyPath, shape) : undefined;
    return <ProceduralAttachment asset={asset} bounds={bounds} tune={tune} />;
  }

  // Skinned attachments — the equipment GLB shares the body's Bip01 rig.
  if (skinned) {
    return (
      <SkinnedAttachment
        parent={parent}
        assetPath={asset.path}
        tintColor={asset.tintColor}
        emissive={asset.emissive}
        emissiveIntensity={asset.emissiveIntensity}
      />
    );
  }

  // Item tuning override: studio-tuned offset / rotation / scale for THIS
  // specific itemId beats whatever defaults the asset def ships with.
  const offset   = itemTune?.offset   ?? asset.offset   ?? defaults.offset;
  const rotation = itemTune?.rotation ?? asset.rotation ?? defaults.rotation;
  const scale    = itemTune?.scale    ?? asset.scale    ?? defaults.scale;

  return (
    <group
      ref={groupRef}
      position={offset}
      rotation={rotation}
      scale={scale}
    >
      <GLTFErrorBoundary key={asset.path} fallback={placeholder}>
        <Suspense fallback={placeholder}>
          {isObjPath(asset.path)
            ? <OBJLoaderEl asset={asset} />
            : <GLTFLoader asset={asset} />}
        </Suspense>
      </GLTFErrorBoundary>
    </group>
  );
}
